import React, { useEffect, useState, useCallback } from "react";
import { Search, X, Link2 } from "lucide-react";
import { apiService } from "../../services/apiService";
const display = (v) => {
  const t = v != null ? String(v).trim() : "";
  return t || "—";
};

/**
 * Unified Master Address modal — four modes:
 * link | change | edit | add
 */
const MasterAddressModal = ({
  mode = "link", // link | change | edit | add
  open,
  onClose,
  onSuccess,
  tenantId,
  tenant,
  inputAddress = null,
  currentMaster = null,
  masterToEdit = null,
}) => {
  const [tab, setTab] = useState("existing"); // existing | create
  const [masterSearch, setMasterSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    std_street_address: "",
    site_name: "",
    std_city: "",
    std_state: "",
    std_zip: "",
    std_country: "US",
  });

  const isFormMode = mode === "edit" || mode === "add" || tab === "create";
  const showTabs = mode === "link" || mode === "change";
  const showInputBox = mode === "link" || mode === "change";

  const kickers = {
    link: "LINK TO A MASTER ADDRESS",
    change: "CHANGE MASTER ADDRESS",
    edit: "EDIT MASTER ADDRESS",
    add: "ADD MASTER ADDRESS",
  };

  const titles = {
    link: inputAddress?.street_address || "Input address",
    change: inputAddress?.street_address || "Input address",
    edit: masterToEdit?.std_street_address || "Master address",
    add: "New master address",
  };

  const subtitles = {
    link: inputAddress?.source_label || "",
    change: currentMaster
      ? `Currently linked to ${currentMaster.std_street_address || "—"}${
          inputAddress?.source_label ? ` · ${inputAddress.source_label}` : ""
        }`
      : inputAddress?.source_label || "",
    edit: masterToEdit
      ? `${masterToEdit.totalCount || masterToEdit.items?.length || 0} input addresses · ${
          masterToEdit.recordCount || 0
        } records`
      : "",
    add: "Inputs can be linked to it afterwards.",
  };

  const loadOptions = useCallback(async () => {
    if (!tenantId || !showTabs || tab !== "existing") return;
    setLoadingOptions(true);
    try {
      const res = await apiService.getMasterAddressOptions(
        tenantId,
        masterSearch
      );
      if (res.success) {
        let list = res.data || [];
        // Pin suggested (link) or current (change) to top
        const pinKey =
          mode === "link"
            ? inputAddress?.suggested_master
              ? [
                  inputAddress.suggested_master.std_street_address,
                  inputAddress.suggested_master.std_city,
                  inputAddress.suggested_master.std_state,
                  inputAddress.suggested_master.std_zip,
                ]
                  .filter(Boolean)
                  .join("|")
              : null
            : currentMaster
              ? [
                  currentMaster.std_street_address,
                  currentMaster.std_city,
                  currentMaster.std_state,
                  currentMaster.std_zip,
                ]
                  .filter(Boolean)
                  .join("|")
              : null;

        if (pinKey) {
          const pinIdx = list.findIndex((o) => o.masterKey === pinKey || [
            o.std_street_address,
            o.std_city,
            o.std_state,
            o.std_zip,
          ]
            .filter(Boolean)
            .join("|") === pinKey);
          if (pinIdx > 0) {
            const [pinned] = list.splice(pinIdx, 1);
            list = [pinned, ...list];
          }
        }
        setOptions(list);
        if (!selectedKey && list.length > 0) {
          const first = list[0];
          setSelectedKey(first.masterKey);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingOptions(false);
    }
  }, [
    tenantId,
    showTabs,
    tab,
    masterSearch,
    mode,
    inputAddress,
    currentMaster,
    selectedKey,
  ]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setTab(mode === "add" || mode === "edit" ? "create" : "existing");
    setMasterSearch("");
    setSelectedKey(null);

    if (mode === "edit" && masterToEdit) {
      setForm({
        std_street_address: masterToEdit.std_street_address || "",
        site_name: masterToEdit.site_name || "",
        std_city: masterToEdit.std_city || "",
        std_state: masterToEdit.std_state || "",
        std_zip: masterToEdit.std_zip || "",
        std_country: masterToEdit.std_country || "US",
      });
    } else if (mode === "add") {
      setForm({
        std_street_address: "",
        site_name: "",
        std_city: "",
        std_state: "",
        std_zip: "",
        std_country: "US",
      });
    } else if (inputAddress) {
      // Prefill create tab from cleaned input
      (async () => {
        try {
          const res = await apiService.normalizeAddressPreview({
            street_address: inputAddress.street_address,
            city: inputAddress.city,
            state: inputAddress.state,
            zip: inputAddress.zip,
            country: inputAddress.country,
          });
          if (res.success && res.data) {
            setForm({
              std_street_address: res.data.std_street_address || "",
              site_name: "",
              std_city: res.data.std_city || "",
              std_state: res.data.std_state || "",
              std_zip: res.data.std_zip || "",
              std_country: res.data.std_country || "US",
            });
          }
        } catch {
          setForm({
            std_street_address: inputAddress.street_address || "",
            site_name: "",
            std_city: inputAddress.city || "",
            std_state: inputAddress.state || "",
            std_zip: inputAddress.zip || "",
            std_country: inputAddress.country || "US",
          });
        }
      })();
    }
  }, [open, mode, masterToEdit, inputAddress]);

  useEffect(() => {
    if (open) loadOptions();
  }, [open, loadOptions]);

  if (!open) return null;

  const primaryLabel =
    mode === "edit"
      ? "Save changes"
      : mode === "add"
        ? "Add master"
        : tab === "create"
          ? "Create master and link"
          : "Link address";

  const footerHint =
    mode === "edit"
      ? "Changes apply everywhere this master is shown, including Detailed Inventory."
      : mode === "change"
        ? "Records move with the input address to the new master."
        : mode === "add"
          ? ""
          : "Linking moves this input's records under the master in Detailed Inventory.";

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (mode === "edit") {
        const res = await apiService.updateMasterAddress({
          tenant_id: tenantId,
          old_master: {
            std_street_address: masterToEdit.std_street_address,
            std_city: masterToEdit.std_city,
            std_state: masterToEdit.std_state,
            std_zip: masterToEdit.std_zip,
            std_country: masterToEdit.std_country,
          },
          new_master: {
            std_street_address: form.std_street_address,
            std_city: form.std_city,
            std_state: form.std_state,
            std_zip: form.std_zip,
            std_country: form.std_country || "US",
          },
          site_name: form.site_name,
        });
        if (!res.success) throw new Error(res.message || "Update failed");
      } else if (mode === "add") {
        const res = await apiService.createMasterAddress({
          tenant_id: tenantId,
          ...form,
          site_name: form.site_name,
        });
        if (!res.success) throw new Error(res.message || "Create failed");
      } else if (tab === "create") {
        const res = await apiService.createMasterAddress({
          tenant_id: tenantId,
          ...form,
          site_name: form.site_name,
          address_ids: inputAddress?.address_id
            ? [inputAddress.address_id]
            : [],
          link_and_create: true,
        });
        if (!res.success) throw new Error(res.message || "Create failed");
      } else {
        const selected = options.find((o) => o.masterKey === selectedKey);
        if (!selected) throw new Error("Select a master address");
        const res = await apiService.linkAddressesToMaster({
          tenantId,
          addressIds: [inputAddress.address_id],
          master: {
            std_street_address: selected.std_street_address,
            std_city: selected.std_city,
            std_state: selected.std_state,
            std_zip: selected.std_zip,
            std_country: selected.std_country || "US",
          },
          howLinked: mode === "change" ? "manual" : "confirmed",
        });
        if (!res.success) throw new Error(res.message || "Link failed");
      }
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const suggestedKey = inputAddress?.suggested_master
    ? [
        inputAddress.suggested_master.std_street_address,
        inputAddress.suggested_master.std_city,
        inputAddress.suggested_master.std_state,
        inputAddress.suggested_master.std_zip,
      ]
        .filter(Boolean)
        .join("|")
    : null;

  const currentKey = currentMaster
    ? [
        currentMaster.std_street_address,
        currentMaster.std_city,
        currentMaster.std_state,
        currentMaster.std_zip,
      ]
        .filter(Boolean)
        .join("|")
    : null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="relative bg-white rounded-lg shadow-xl w-full max-w-[760px] max-h-[90vh] flex flex-col"
        role="dialog"
        aria-modal="true"
      >
        <div className="px-6 pt-5 pb-3 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold tracking-wide text-brand-red uppercase">
              {kickers[mode]}
            </div>
            <h2 className="text-xl font-bold text-gray-900 mt-1 truncate">
              {titles[mode]}
            </h2>
            {subtitles[mode] ? (
              <p className="text-sm text-gray-500 mt-0.5">{subtitles[mode]}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {showInputBox && inputAddress && (
            <div className="mb-4">
              <div className="text-xs text-gray-500 mb-1.5">
                Input address{" "}
                <span className="text-gray-400">as received, read only</span>
              </div>
              <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 grid grid-cols-5 gap-3 text-sm">
                {[
                  ["STREET", inputAddress.street_address],
                  ["CITY", inputAddress.city],
                  ["STATE", inputAddress.state],
                  ["ZIP", inputAddress.zip],
                  ["COUNTRY", inputAddress.country],
                ].map(([label, val]) => (
                  <div key={label}>
                    <div className="text-[10px] font-semibold text-gray-400 tracking-wide">
                      {label}
                    </div>
                    <div className="text-gray-900 font-medium mt-0.5 break-words">
                      {display(val)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showTabs && (
            <div className="flex gap-6 border-b border-gray-200 mb-4">
              {[
                ["existing", "Link to existing master"],
                ["create", "Create new master"],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={`pb-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    tab === id
                      ? "border-brand-red text-brand-red"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {isFormMode ? (
            <div className="space-y-3">
              {(mode === "edit" || tab === "create") && (
                <p className="text-sm text-gray-500">
                  Prefilled with a cleaned version of the input address. Adjust
                  anything before saving.
                </p>
              )}
              <label className="block">
                <span className="text-[10px] font-semibold text-gray-500 tracking-wide">
                  STREET ADDRESS
                </span>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-red focus:border-brand-red"
                  value={form.std_street_address}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      std_street_address: e.target.value,
                    }))
                  }
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold text-gray-500 tracking-wide">
                  SITE NAME{" "}
                  <span className="font-normal text-gray-400">optional</span>
                </span>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-red focus:border-brand-red"
                  placeholder="e.g. Cloverleaf"
                  value={form.site_name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, site_name: e.target.value }))
                  }
                />
              </label>
              <div className="grid grid-cols-12 gap-3">
                <label className="block col-span-5">
                  <span className="text-[10px] font-semibold text-gray-500 tracking-wide">
                    CITY
                  </span>
                  <input
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-red focus:border-brand-red"
                    value={form.std_city}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, std_city: e.target.value }))
                    }
                  />
                </label>
                <label className="block col-span-2">
                  <span className="text-[10px] font-semibold text-gray-500 tracking-wide">
                    STATE
                  </span>
                  <input
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-red focus:border-brand-red"
                    value={form.std_state}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, std_state: e.target.value }))
                    }
                  />
                </label>
                <label className="block col-span-3">
                  <span className="text-[10px] font-semibold text-gray-500 tracking-wide">
                    ZIP
                  </span>
                  <input
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-red focus:border-brand-red"
                    value={form.std_zip}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, std_zip: e.target.value }))
                    }
                  />
                </label>
                <label className="block col-span-2">
                  <span className="text-[10px] font-semibold text-gray-500 tracking-wide">
                    COUNTRY
                  </span>
                  <input
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-red focus:border-brand-red"
                    value={form.std_country}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, std_country: e.target.value }))
                    }
                  />
                </label>
              </div>
            </div>
          ) : (
            <div>
              <div className="relative mb-3">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  placeholder="Search masters by street, city, ZIP or site name"
                  className="w-full border border-gray-300 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-red focus:border-brand-red"
                  value={masterSearch}
                  onChange={(e) => setMasterSearch(e.target.value)}
                />
              </div>
              <div className="border border-gray-200 rounded-md max-h-[280px] overflow-y-auto divide-y divide-gray-100">
                {loadingOptions ? (
                  <div className="p-4 text-sm text-gray-500">Loading…</div>
                ) : options.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">
                    No masters found.
                  </div>
                ) : (
                  options.map((opt) => {
                    const selected = selectedKey === opt.masterKey;
                    const isSuggested =
                      suggestedKey &&
                      (opt.masterKey === suggestedKey ||
                        [
                          opt.std_street_address,
                          opt.std_city,
                          opt.std_state,
                          opt.std_zip,
                        ]
                          .filter(Boolean)
                          .join("|") === suggestedKey);
                    const isCurrent =
                      currentKey &&
                      (opt.masterKey === currentKey ||
                        [
                          opt.std_street_address,
                          opt.std_city,
                          opt.std_state,
                          opt.std_zip,
                        ]
                          .filter(Boolean)
                          .join("|") === currentKey);
                    return (
                      <label
                        key={opt.masterKey}
                        className={`flex items-start gap-3 px-3 py-3 cursor-pointer ${
                          selected
                            ? "bg-red-50 border-l-2 border-brand-red"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="master-option"
                          className="mt-1 accent-[#a51c1c]"
                          checked={selected}
                          onChange={() => setSelectedKey(opt.masterKey)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-gray-900">
                              {display(opt.std_street_address)}
                            </span>
                            {opt.site_name ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                {opt.site_name}
                              </span>
                            ) : null}
                            {isSuggested ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                                Suggested
                              </span>
                            ) : null}
                            {isCurrent ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                                Current
                              </span>
                            ) : null}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5">
                            {[opt.std_city, opt.std_state, opt.std_zip]
                              .filter(Boolean)
                              .join(", ")}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 whitespace-nowrap pt-1">
                          {opt.input_count || 0} inputs · {opt.record_count || 0}{" "}
                          records
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500 flex-1">{footerHint}</p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md text-white bg-brand-red hover:bg-red-800 disabled:opacity-50"
            >
              {tab === "existing" && (mode === "link" || mode === "change") ? (
                <Link2 className="w-4 h-4" />
              ) : null}
              {saving ? "Saving…" : primaryLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterAddressModal;
